import React, { useState, useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Button, Input, Form, Select, Row, Col, Tag, Typography, notification, DatePicker } from 'antd';
import { SearchOutlined, DeleteOutlined } from '@ant-design/icons';
import { TableCustom } from '../../../components/TableCustom';
import { useMutation, useQuery } from '@tanstack/react-query';
import { 
  getListHistoryPremiumsRequest, 
  changeHistoryPremiumStatusRequest,
  listPaymentChannelsRequest,
  listAppVersionsRequest,
  listGroupServicesRequest
} from '../../../services/cms/historyPremiumServiceRequest';
import { listActivePremiumTypesRequest } from '../../../services/cms/premiumTypeServiceRequest';
import { newObjectKeys } from '../../../utils/utility';

const { Text } = Typography;

const STATUS_OPTIONS = [
  { value: '', label: 'Tất cả' },
  { value: 1, label: 'Thành công' },
  { value: 2, label: 'Đã Hoàn tiền' },
  { value: 0, label: 'Fake' },
  { value: 3, label: 'Hủy giao dịch' },
];

const PLATFORM_OPTIONS = [
  { value: '', label: 'Tất cả' },
  { value: 'ios', label: 'iOS' },
  { value: 'android', label: 'Android' },
  { value: 'web', label: 'Web' },
  { value: 'cms', label: 'CMS' },
  { value: 'partner', label: 'Partner' },
  { value: 'unkown', label: 'Unknown' },
  { value: 'activecall', label: 'Active Call' },
];

const QuanLyHistoryPremium = () => {
  const [searchParams] = useSearchParams();
  const [formSearch] = Form.useForm();

  // Dynamic filter options states
  const [paymentChannels, setPaymentChannels] = useState([]);
  const [appVersions, setAppVersions] = useState([]);
  const [services, setServices] = useState([]);
  const [premiumTypes, setPremiumTypes] = useState([]);

  // Fetch filters options on mount
  useEffect(() => {
    listPaymentChannelsRequest().then((res) => {
      if (Array.isArray(res)) {
        setPaymentChannels(res.map(c => ({ value: c.name, label: c.description || c.name })));
      }
    });
    listAppVersionsRequest().then((res) => {
      if (Array.isArray(res)) {
        setAppVersions(res.map(v => ({ value: v, label: v })));
      }
    });
    listGroupServicesRequest().then((res) => {
      if (Array.isArray(res)) {
        setServices(res);
      }
    });
    listActivePremiumTypesRequest().then((res) => {
      if (Array.isArray(res)) {
        setPremiumTypes(res.map(pt => ({ value: pt.id, label: pt.description || pt.name })));
      }
    });
  }, []);

  const paymentChannelOptions = useMemo(() => [
    { value: '', label: 'Tất cả' },
    ...paymentChannels,
  ], [paymentChannels]);

  const appVersionOptions = useMemo(() => [
    { value: '', label: 'Tất cả' },
    ...appVersions,
  ], [appVersions]);

  const serviceOptions = useMemo(() => [
    { value: '', label: 'Tất cả' },
    ...services,
  ], [services]);

  const premiumTypeOptions = useMemo(() => [
    { value: '', label: 'Tất cả' },
    ...premiumTypes,
  ], [premiumTypes]);

  // Đọc query params từ URL để hỗ trợ deep-linking
  const initialParams = useMemo(() => {
    const init = { page: 1, limit: 20 };
    const userIdParam = searchParams.get('user_id');
    if (userIdParam) init.user_id = userIdParam;
    return init;
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const [params, setParams] = useState(initialParams);

  // Set giá trị ban đầu vào form search nếu có query param
  useEffect(() => {
    const userIdParam = searchParams.get('user_id');
    if (userIdParam) {
      formSearch.setFieldsValue({ user_id: userIdParam });
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const { data: dataHistory, refetch, isLoading: isLoadingHistory } = useQuery({
    queryKey: ['dataHistoryPremiums', params],
    queryFn: () => getListHistoryPremiumsRequest(params),
  });

  const { mutate: mutateChangeStatus } = useMutation({
    mutationFn: changeHistoryPremiumStatusRequest,
    onSuccess: (data) => {
      if (data?.status === 1) { notification.success({ message: 'Cập nhật trạng thái thành công!' }); refetch(); }
      else notification.error({ message: data?.message || 'Có lỗi xảy ra!' });
    },
  });

  const handleSubmitForm = (values) => {
    const temp = { page: 1, limit: params.limit };
    if (values?.user_id) temp.user_id = values.user_id;
    if (values?.transaction_id) temp.transaction_id = values.transaction_id;
    if (values?.status !== undefined && values?.status !== '') temp.status = values.status;
    if (values?.payment_channel) temp.payment_channel = values.payment_channel;
    if (values?.platform) temp.platform = values.platform;
    if (values?.date_from) temp.date_from = values.date_from.format('YYYY-MM-DD');
    if (values?.date_to) temp.date_to = values.date_to.format('YYYY-MM-DD');
    if (values?.service_id) temp.service_id = values.service_id;
    if (values?.premium_type_id) temp.premium_type_id = values.premium_type_id;
    if (values?.service_type !== undefined && values?.service_type !== '') temp.service_type = values.service_type;
    if (values?.is_test !== undefined && values?.is_test !== '') temp.is_test = values.is_test;
    if (values?.app_version) temp.app_version = values.app_version;
    setParams(temp);
  };

  const columns = [
    { title: 'ID', dataIndex: 'id', key: 'id', width: 70 },
    { title: 'User ID', dataIndex: 'user_id', key: 'user_id', width: 90 },
    { title: 'Transaction ID', dataIndex: 'transaction_id', key: 'transaction_id', width: 200,
      render: (text) => <Text style={{ fontSize: 12, wordBreak: 'break-all' }}>{text}</Text>,
    },
    { title: 'Thời gian GD', dataIndex: 'transaction_time', key: 'transaction_time', width: 150 },
    { title: 'Giá trị', dataIndex: 'transaction_value', key: 'transaction_value', width: 120,
      render: (v) => v ? `${Number(v).toLocaleString('vi-VN')}` : '--',
    },
    { title: 'Gói', dataIndex: 'premium_type_name', key: 'premium_type_name', width: 150 },
    { title: 'Dịch vụ', dataIndex: 'service_name', key: 'service_name', width: 120 },
    { title: 'Kênh TT', key: 'payment_channel', dataIndex: 'payment_channel_name', width: 150,
      render: (v, r) => v || r.payment_channel || '--',
    },
    { title: 'Platform', key: 'platform', dataIndex: 'platform_name', width: 90,
      render: (v, r) => v || r.platform || '--',
    },
    {
      title: 'Trạng thái', dataIndex: 'status', key: 'status', width: 130,
      render: (status, record) => (
        <Select size="small" value={status} style={{ width: 120 }}
          onChange={(val) => mutateChangeStatus({ id: record.id, status: val })}
          options={STATUS_OPTIONS.filter(o => o.value !== '')}
        />
      ),
    },
  ];

  return (
    <div className="m-5">
      <div className="flex justify-between items-center mb-5">
        <h1 className="text-2xl">Lịch sử giao dịch Premium</h1>
        {dataHistory?.totalPayment > 0 && (
          <Tag color="blue" style={{ fontSize: 14, padding: '4px 12px' }}>
            Tổng thanh toán: {Number(dataHistory.totalPayment).toLocaleString('vi-VN')} VND
          </Tag>
        )}
      </div>
      <Form name="search_history_premiums" form={formSearch} layout="vertical" requiredMark={false} className="mb-5" onFinish={handleSubmitForm}>
        <Row gutter={[12, 0]}>
          <Col xs={24} sm={12} md={6} xl={4}>
            <Form.Item name="user_id" label={<Text>User ID</Text>}>
              <Input placeholder="User ID" type="number" allowClear />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12} md={6} xl={4}>
            <Form.Item name="transaction_id" label={<Text>Transaction ID</Text>}>
              <Input placeholder="Transaction ID" allowClear />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12} md={6} xl={4}>
            <Form.Item name="status" label={<Text>Trạng thái</Text>}>
              <Select allowClear placeholder="Tất cả" options={STATUS_OPTIONS} />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12} md={6} xl={4}>
            <Form.Item name="payment_channel" label={<Text>Kênh thanh toán</Text>}>
              <Select allowClear placeholder="Tất cả" options={paymentChannelOptions} showSearch optionFilterProp="label" />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12} md={6} xl={4}>
            <Form.Item name="platform" label={<Text>Platform</Text>}>
              <Select allowClear placeholder="Tất cả" options={PLATFORM_OPTIONS} />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12} md={6} xl={4}>
            <Form.Item name="service_id" label={<Text>Dịch vụ</Text>}>
              <Select allowClear placeholder="Tất cả" options={serviceOptions} showSearch optionFilterProp="label" />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12} md={6} xl={4}>
            <Form.Item name="premium_type_id" label={<Text>Gói mua</Text>}>
              <Select allowClear placeholder="Tất cả" options={premiumTypeOptions} showSearch optionFilterProp="label" />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12} md={6} xl={4}>
            <Form.Item name="service_type" label={<Text>Loại dịch vụ</Text>}>
              <Select allowClear placeholder="Tất cả" options={[
                { value: '', label: 'Tất cả' },
                { value: 1, label: 'Chuyên gia 1-1' },
                { value: 2, label: 'InApp' },
              ]} />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12} md={6} xl={4}>
            <Form.Item name="is_test" label={<Text>Loại giao dịch</Text>}>
              <Select allowClear placeholder="Tất cả" options={[
                { value: '', label: 'Tất cả' },
                { value: 0, label: 'Thật' },
                { value: 1, label: 'Test' },
              ]} />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12} md={6} xl={4}>
            <Form.Item name="app_version" label={<Text>App Version</Text>}>
              <Select allowClear placeholder="Tất cả" options={appVersionOptions} showSearch optionFilterProp="label" />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12} md={6} xl={4}>
            <Form.Item name="date_from" label={<Text>GD từ ngày</Text>}>
              <DatePicker format="DD/MM/YYYY" style={{ width: '100%' }} placeholder="Từ ngày" />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12} md={6} xl={4}>
            <Form.Item name="date_to" label={<Text>GD đến ngày</Text>}>
              <DatePicker format="DD/MM/YYYY" style={{ width: '100%' }} placeholder="Đến ngày" />
            </Form.Item>
          </Col>
          <Col className="flex items-center gap-2" xs={24} style={{ marginTop: 24, marginBottom: 12 }}>
            <Button type="primary" htmlType="submit"><SearchOutlined /> Tìm kiếm</Button>
            {params && Object.values(newObjectKeys(params, ['user_id', 'transaction_id', 'status', 'payment_channel', 'platform', 'date_from', 'date_to', 'service_id', 'premium_type_id', 'service_type', 'is_test', 'app_version'])).some((value) => value !== undefined && value !== null && value !== '') && (
              <Button htmlType="button" onClick={() => { formSearch.resetFields(); setParams({ page: 1, limit: params.limit }); }}>
                <DeleteOutlined /> Xóa lọc
              </Button>
            )}
          </Col>
        </Row>
      </Form>
      <TableCustom columns={columns} dataSource={dataHistory?.data} totalItem={dataHistory?.total} params={params} setParams={setParams}
        loading={isLoadingHistory} scroll={{ x: 1200 }} />
    </div>
  );
};

export default QuanLyHistoryPremium;
